import { httpClient } from '@/api/http-client';
import type {
  Partner,
  Commission,
  PayoutBatch,
  Campaign,
  FraudAlert,
  FraudStats,
  PaginatedResponse,
  KYCData,
  PartnerLevel,
  PartnerAnalyticsOverview,
  PartnerAnalyticsFunnel,
  FinancialTrendPoint,
  CacComparisonResponse,
} from '../types/admin-partner';

export const adminPartnerService = {
  getPartners: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    kycStatus?: string;
    level?: string;
    search?: string;
    from?: string;
    to?: string;
  }): Promise<PaginatedResponse<Partner>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.status) searchParams.set('status', params.status);
    if (params?.kycStatus) searchParams.set('kycStatus', params.kycStatus);
    if (params?.level) searchParams.set('level', params.level);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    const query = searchParams.toString();
    return httpClient.get<PaginatedResponse<Partner>>(`/admin/partners${query ? `?${query}` : ''}`);
  },

  getPartnerById: async (id: string): Promise<Partner> => {
    return httpClient.get<Partner>(`/admin/partners/${id}`);
  },

  approvePartner: async (id: string): Promise<Partner> => {
    return httpClient.patch<Partner>(`/admin/partners/${id}/approve`, {});
  },

  rejectPartner: async (id: string, reason?: string): Promise<Partner> => {
    return httpClient.patch<Partner>(`/admin/partners/${id}/reject`, { reason });
  },

  suspendPartner: async (id: string, reason?: string): Promise<Partner> => {
    return httpClient.patch<Partner>(`/admin/partners/${id}/suspend`, { reason });
  },

  activatePartner: async (id: string): Promise<Partner> => {
    return httpClient.patch<Partner>(`/admin/partners/${id}/activate`, {});
  },

  updatePartnerKYC: async (id: string, kycData: Partial<KYCData>): Promise<Partner> => {
    return httpClient.patch<Partner>(`/admin/partners/${id}/kyc`, kycData);
  },

  changePartnerLevel: async (id: string, level: PartnerLevel): Promise<Partner> => {
    return httpClient.patch<Partner>(`/admin/partners/${id}/level`, { level });
  },

  getCommissions: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    partnerId?: string;
    from?: string;
    to?: string;
  }): Promise<PaginatedResponse<Commission>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.status) searchParams.set('status', params.status);
    if (params?.partnerId) searchParams.set('partnerId', params.partnerId);
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    const query = searchParams.toString();
    return httpClient.get<PaginatedResponse<Commission>>(`/admin/commissions${query ? `?${query}` : ''}`);
  },

  getCommissionById: async (id: string): Promise<Commission> => {
    return httpClient.get<Commission>(`/admin/commissions/${id}`);
  },

  approveCommission: async (id: string): Promise<Commission> => {
    return httpClient.patch<Commission>(`/admin/commissions/${id}/approve`, {});
  },

  cancelCommission: async (id: string, reason?: string): Promise<Commission> => {
    return httpClient.patch<Commission>(`/admin/commissions/${id}/cancel`, { reason });
  },

  blockCommission: async (id: string, reason?: string): Promise<Commission> => {
    return httpClient.patch<Commission>(`/admin/commissions/${id}/block`, { reason });
  },

  processCommissionExpiration: async (): Promise<{ processed: number }> => {
    return httpClient.post<{ processed: number }>('/admin/commissions/process-expiration');
  },

  bulkApproveCommissions: async (ids: string[]): Promise<{ approved: number }> => {
    return httpClient.post<{ approved: number }>('/admin/commissions/bulk-approve', { ids });
  },

  getPayoutBatches: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<PaginatedResponse<PayoutBatch>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.status) searchParams.set('status', params.status);
    const query = searchParams.toString();
    return httpClient.get<PaginatedResponse<PayoutBatch>>(`/admin/affiliate-payouts/batches${query ? `?${query}` : ''}`);
  },

  generatePayoutBatch: async (params?: {
    partnerIds?: string[];
    minAmount?: number;
  }): Promise<PayoutBatch> => {
    return httpClient.post<PayoutBatch>('/admin/affiliate-payouts/batches/generate', params || {});
  },

  processPayoutBatch: async (batchId: string): Promise<PayoutBatch> => {
    return httpClient.post<PayoutBatch>(`/admin/affiliate-payouts/batches/${batchId}/process`, {});
  },

  getCampaigns: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<PaginatedResponse<Campaign>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.status) searchParams.set('status', params.status);
    const query = searchParams.toString();
    return httpClient.get<PaginatedResponse<Campaign>>(`/admin/campaigns${query ? `?${query}` : ''}`);
  },

  getCampaignById: async (id: string): Promise<Campaign> => {
    return httpClient.get<Campaign>(`/admin/campaigns/${id}`);
  },

  createCampaign: async (data: {
    name: string;
    description?: string;
    commissionRate: number;
    startDate: string;
    endDate?: string;
  }): Promise<Campaign> => {
    return httpClient.post<Campaign>('/admin/campaigns', data);
  },

  updateCampaign: async (id: string, data: Partial<Campaign>): Promise<Campaign> => {
    return httpClient.patch<Campaign>(`/admin/campaigns/${id}`, data);
  },

  getFraudAlerts: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    level?: string;
  }): Promise<PaginatedResponse<FraudAlert>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.status) searchParams.set('status', params.status);
    if (params?.level) searchParams.set('level', params.level);
    const query = searchParams.toString();
    return httpClient.get<PaginatedResponse<FraudAlert>>(`/admin/fraud${query ? `?${query}` : ''}`);
  },

  getFraudAlertById: async (id: string): Promise<FraudAlert> => {
    return httpClient.get<FraudAlert>(`/admin/fraud/${id}`);
  },

  reviewFraudAlert: async (
    id: string,
    data: {
      status: 'REVIEWED' | 'DISMISSED';
      notes?: string;
    }
  ): Promise<FraudAlert> => {
    return httpClient.patch<FraudAlert>(`/admin/fraud/${id}/review`, data);
  },

  getFraudStats: async (): Promise<FraudStats> => {
    return httpClient.get<FraudStats>('/admin/fraud/stats');
  },

  getAnalyticsOverview: async (params?: {
    startDate?: string;
    endDate?: string;
    serviceType?: string;
    partnerId?: string;
  }): Promise<PartnerAnalyticsOverview> => {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    if (params?.serviceType) searchParams.set('serviceType', params.serviceType);
    if (params?.partnerId) searchParams.set('partnerId', params.partnerId);
    const query = searchParams.toString();
    return httpClient.get<PartnerAnalyticsOverview>(`/admin/partners/analytics/overview${query ? `?${query}` : ''}`);
  },

  getAnalyticsFunnel: async (params?: {
    startDate?: string;
    endDate?: string;
    partnerId?: string;
  }): Promise<PartnerAnalyticsFunnel> => {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    if (params?.partnerId) searchParams.set('partnerId', params.partnerId);
    const query = searchParams.toString();
    return httpClient.get<PartnerAnalyticsFunnel>(`/admin/partners/analytics/funnel${query ? `?${query}` : ''}`);
  },

  getFinancialTrend: async (params?: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'DAY' | 'MONTH';
  }): Promise<FinancialTrendPoint[]> => {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    if (params?.groupBy) searchParams.set('groupBy', params.groupBy);
    const query = searchParams.toString();
    return httpClient.get<FinancialTrendPoint[]>(`/admin/partners/analytics/financial-trend${query ? `?${query}` : ''}`);
  },

  getCacComparison: async (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<CacComparisonResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    const query = searchParams.toString();
    return httpClient.get<CacComparisonResponse>(`/admin/partners/analytics/cac-comparison${query ? `?${query}` : ''}`);
  },
};
